/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useListInitiativeAppIntegrationsQuery } from '@api/initiative-app-integrations';
import Dashboard from '@g17eco/molecules/dashboard';
import { Loader } from '@g17eco/atoms/loader';
import { JSX } from 'react';
import { useHistory } from 'react-router-dom';
import { ProviderCardExtended, ProviderCard } from '@features/integrations';
import { IntegrationCodes, ProviderIntegrationStatus } from '@g17eco/types/app-integration';
import { BasicAlert } from '@g17eco/molecules/alert';
import { AdminBreadcrumbsProps } from '@g17eco/molecules/breadcrumbs';
import { IntegrationList } from './IntegrationList';
import { IntegrationModal } from './IntegrationModal';
import { useIntegrationModal } from '@features/integrations/hooks/useIntegrationModal';
import { ProductCardFooter } from '@routes/home/flavours/partials/ProductCards';
import config from '../../config';
import { generateRedirectUrl, RedirectPage } from '../../routes/redirect/util';

interface Props {
  initiativeId: string;
  additionalCards: {
    active: ProviderCard[];
    available: ProviderCard[];
  };
  BreadCrumbsComponent: (props: AdminBreadcrumbsProps) => JSX.Element;
}

const emissionTrackerLogo = `${config.media.cdnUrl}/carbon-calculators/Emissions_Tracker_logo.svg`;

export const IntegrationsDashboard = ({ initiativeId, additionalCards, BreadCrumbsComponent }: Props) => {
  const history = useHistory();
  const { code, setCode, onLinkClick } = useIntegrationModal(initiativeId);

  const { data, isLoading, error } = useListInitiativeAppIntegrationsQuery(initiativeId);

  const { allProviders = [], usedProviders = [] } = data ?? {};

  const usedProviderCards: ProviderCardExtended[] = usedProviders.map((provider) => {
    const onClick = () => onLinkClick({ link: provider.login?.url, code: provider.code });
    const buttons = [];

    if (provider.status === ProviderIntegrationStatus.Active) {
      buttons.push({ text: 'details', onClick: () => setCode(provider.code) }, { text: 'access', onClick });
    }

    const logo = Object.values(IntegrationCodes).includes(provider.code as IntegrationCodes)
      ? emissionTrackerLogo
      : provider.logo;

    return { ...provider, logo, buttons, onClick, footer: <ProductCardFooter imageUrl={provider.logoFooter} /> };
  });

  const hasEmissionsTracker = allProviders.some((provider) =>
    Object.values(IntegrationCodes).includes(provider.code as IntegrationCodes),
  );

  const providerCards: ProviderCard[] = hasEmissionsTracker
    ? [
        {
          logo: emissionTrackerLogo,
          buttons: [
            {
              text: 'Info',
              onClick: () => {
                history.push(generateRedirectUrl({ page: RedirectPage.EmissionsCalculator, initiativeId }));
              },
            },
          ],
        },
      ]
    : [];

  if (isLoading) {
    return <Loader />;
  }

  const activeCards: ProviderCard[] = [...additionalCards.active, ...usedProviderCards];
  const availableCards: ProviderCard[] = [...additionalCards.available, ...providerCards];

  return (
    <Dashboard>
      <div className='pl-2 pb-4'>
        <BreadCrumbsComponent initiativeId={initiativeId} breadcrumbs={[{ label: 'App integrations' }]} />
      </div>
      {error ? <BasicAlert type='danger'>{'message' in error ? error.message : error}</BasicAlert> : null}
      <IntegrationList cards={activeCards} title={'Active app integrations'} />
      <IntegrationList cards={availableCards} title={'Available app integrations'} />
      <IntegrationModal
        code={code}
        initiativeId={initiativeId}
        closeModal={() => setCode(undefined)}
        handleLinkClick={onLinkClick}
      />
    </Dashboard>
  );
};
